import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import FilterCell from '../Common/FilterCell';
import {
  MOCK_REPORT_EMAIL_RECIPIENTS,
  MOCK_REPORT_REQUEST_TYPES,
  DOCUMENTS_SEND_UPDATE_API,
  getReportRecipientEmail,
  getReportRecipientLabel,
  getReportRequestTypeLabel,
  mockSendReportUpdateEmail,
} from './reportEmailConstants';
import './BoqEmailRequestDialog.css';
import '../Common/DataTable.css';

const formatCellValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
};

const isFilterableColumn = (col) => col.type !== 'sum' && col.type !== 'link';

const applyRowFilters = (entries, filters) => {
  let filtered = entries;

  Object.entries(filters).forEach(([field, filterObj]) => {
    if (!filterObj) return;
    if (filterObj.select && Array.isArray(filterObj.select) && filterObj.select.length > 0) {
      filtered = filtered.filter(({ row }) => filterObj.select.includes(String(row[field] ?? '')));
    }
    if (filterObj.input) {
      const query = filterObj.input.toLowerCase();
      filtered = filtered.filter(({ row }) => String(row[field] ?? '').toLowerCase().includes(query));
    }
  });

  return filtered;
};

const applyRowSort = (entries, sort) => {
  if (!sort.field) return entries;

  return [...entries].sort((a, b) => {
    const av = a.row[sort.field];
    const bv = b.row[sort.field];
    if (av === bv) return 0;
    if (sort.direction === 'asc') return av > bv ? 1 : -1;
    return av < bv ? 1 : -1;
  });
};

export default function ReportEmailRequestDialog({
  open,
  onClose,
  rows = [],
  tableColumns = [],
  contractId = '',
  contractLabel = '',
  sendApiHint = DOCUMENTS_SEND_UPDATE_API,
  onSent,
}) {
  const [step, setStep] = useState('form');
  const [requestType, setRequestType] = useState(MOCK_REPORT_REQUEST_TYPES[0].key);
  const [recipientKey, setRecipientKey] = useState(MOCK_REPORT_EMAIL_RECIPIENTS[0].key);
  const [generalNote, setGeneralNote] = useState('');
  const [rowState, setRowState] = useState({});
  const [filters, setFilters] = useState({});
  const [sort, setSort] = useState({ field: null, direction: null });
  const [sending, setSending] = useState(false);

  const rowEntries = useMemo(
    () => rows.map((row, index) => ({ row, index, rowKey: `row-${index}` })),
    [rows]
  );

  useEffect(() => {
    if (!open) return;

    const initial = {};
    rowEntries.forEach(({ rowKey }) => {
      initial[rowKey] = { selected: false, rowNote: '' };
    });

    setRowState(initial);
    setStep('form');
    setRequestType(MOCK_REPORT_REQUEST_TYPES[0].key);
    setRecipientKey(MOCK_REPORT_EMAIL_RECIPIENTS[0].key);
    setGeneralNote('');
    setFilters({});
    setSort({ field: null, direction: null });
    setSending(false);
  }, [open, rowEntries]);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.classList.add('report-email-dialog-open');

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('report-email-dialog-open');
    };
  }, [open, onClose]);

  const filteredRowEntries = useMemo(
    () => applyRowSort(applyRowFilters(rowEntries, filters), sort),
    [rowEntries, filters, sort]
  );

  const selectedEntries = useMemo(
    () => rowEntries.filter(({ rowKey }) => rowState[rowKey]?.selected),
    [rowEntries, rowState]
  );

  const selectedCount = selectedEntries.length;
  const canProceed = selectedCount > 0;

  const toggleRow = (rowKey, selected) => {
    setRowState((prev) => ({
      ...prev,
      [rowKey]: { ...prev[rowKey], selected },
    }));
  };

  const setRowNote = (rowKey, rowNote) => {
    setRowState((prev) => ({
      ...prev,
      [rowKey]: { ...prev[rowKey], rowNote },
    }));
  };

  const selectAllVisible = useCallback(() => {
    setRowState((prev) => {
      const next = { ...prev };
      filteredRowEntries.forEach(({ rowKey }) => {
        next[rowKey] = { ...next[rowKey], selected: true };
      });
      return next;
    });
  }, [filteredRowEntries]);

  const clearSelection = useCallback(() => {
    setRowState((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        next[key] = { ...next[key], selected: false };
      });
      return next;
    });
  }, []);

  const handleSort = (field) => {
    setSort((prev) => {
      if (prev.field === field) {
        if (prev.direction === 'asc') return { field, direction: 'desc' };
        if (prev.direction === 'desc') return { field: null, direction: null };
      }
      return { field, direction: 'asc' };
    });
  };

  const handleFilter = (field, type, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: { ...prev[field], [type]: value },
    }));
  };

  const buildPayload = () => ({
    contractId,
    requestType,
    recipientKey,
    recipientEmail: getReportRecipientEmail(recipientKey),
    generalNote: generalNote.trim(),
    items: selectedEntries.map(({ row, index, rowKey }) => ({
      rowIndex: index,
      values: tableColumns.reduce((acc, col) => {
        acc[col.field] = row[col.field];
        return acc;
      }, {}),
      rowNote: (rowState[rowKey]?.rowNote || '').trim(),
    })),
  });

  const handleConfirmSend = async () => {
    const payload = buildPayload();
    setSending(true);
    setStep('sending');

    try {
      const result = await mockSendReportUpdateEmail(payload);
      onSent?.({
        ...result,
        payload,
        requestTypeLabel: getReportRequestTypeLabel(requestType),
        recipientLabel: getReportRecipientLabel(recipientKey),
      });
      onClose();
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  const projectLine = contractLabel || contractId || '—';
  const allVisibleSelected = filteredRowEntries.length > 0
    && filteredRowEntries.every(({ rowKey }) => rowState[rowKey]?.selected);
  const hasActiveFilters = Object.values(filters).some(
    (filterObj) => (filterObj?.select?.length > 0) || filterObj?.input
  );
  const visibleCountLabel = filteredRowEntries.length !== rowEntries.length
    ? `${filteredRowEntries.length} מוצגות מתוך ${rows.length}`
    : `${rows.length} שורות`;

  return createPortal(
    <div className="boq-email-dialog-overlay" role="presentation" onClick={onClose}>
      <div
        className="boq-email-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-email-dialog-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="boq-email-dialog-header">
          <div>
            <h2 id="report-email-dialog-title" className="boq-email-dialog-title">
              שליחה במייל לעדכון
            </h2>
            <p className="boq-email-dialog-subtitle">
              פרויקט: {projectLine}
              {' · '}
              <span className="boq-email-dialog-mock-badge">Mock – ללא שליחה אמיתית</span>
            </p>
          </div>
          <button type="button" className="boq-email-dialog-close" onClick={onClose} aria-label="סגור">
            ×
          </button>
        </div>

        {step === 'form' && (
          <>
            <div className="boq-email-dialog-body">
              <div className="boq-email-dialog-form-row">
                <div className="boq-email-dialog-field boq-email-dialog-field--compact">
                  <label htmlFor="report-email-request-type">סוג בקשה</label>
                  <select
                    id="report-email-request-type"
                    value={requestType}
                    onChange={(e) => setRequestType(e.target.value)}
                  >
                    {MOCK_REPORT_REQUEST_TYPES.map((type) => (
                      <option key={type.key} value={type.key}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="boq-email-dialog-field boq-email-dialog-field--compact">
                  <label htmlFor="report-email-recipient">נמען</label>
                  <select
                    id="report-email-recipient"
                    value={recipientKey}
                    onChange={(e) => setRecipientKey(e.target.value)}
                  >
                    {MOCK_REPORT_EMAIL_RECIPIENTS.map((recipient) => (
                      <option key={recipient.key} value={recipient.key}>
                        {recipient.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="boq-email-dialog-field boq-email-dialog-field--note">
                  <label htmlFor="report-email-general-note">הערה כללית</label>
                  <input
                    id="report-email-general-note"
                    type="text"
                    className="boq-email-dialog-note-input"
                    value={generalNote}
                    onChange={(e) => setGeneralNote(e.target.value)}
                    placeholder="לדוגמה: נא לעדכן סטטוס משלוח למסמכים המסומנים"
                  />
                </div>
              </div>

              <div className="boq-email-dialog-toolbar">
                <div className="boq-email-dialog-toolbar-left">
                  <button type="button" className="boq-btn boq-btn-small" onClick={selectAllVisible}>
                    {hasActiveFilters ? 'בחר הכל (מסונן)' : 'בחר הכל'}
                  </button>
                  <button type="button" className="boq-btn boq-btn-small boq-btn-secondary" onClick={clearSelection}>
                    נקה בחירה
                  </button>
                </div>
                <span className="boq-email-dialog-count">
                  נבחרו <strong>{selectedCount}</strong> · {visibleCountLabel}
                </span>
              </div>

              <div className="boq-email-dialog-table-wrap">
                {rowEntries.length === 0 ? (
                  <div className="boq-email-dialog-empty">אין שורות להצגה</div>
                ) : filteredRowEntries.length === 0 ? (
                  <div className="boq-email-dialog-empty">אין שורות התואמות לסינון</div>
                ) : (
                  <table className="boq-email-dialog-table">
                    <thead>
                      <tr>
                        <th className="col-check">
                          <input
                            type="checkbox"
                            aria-label="בחר הכל בטבלה"
                            checked={allVisibleSelected}
                            onChange={(e) => (e.target.checked ? selectAllVisible() : clearSelection())}
                          />
                        </th>
                        {tableColumns.map((col) => (
                          <th
                            key={col.columnId || col.field}
                            className="boq-email-dialog-th"
                            onDoubleClick={() => handleSort(col.field)}
                            title="לחיצה כפולה למיון"
                          >
                            <div className="boq-email-dialog-th-content">
                              <span>{col.label || col.field}</span>
                              <div className="boq-email-dialog-th-actions">
                                {sort.field === col.field && (
                                  <span className="boq-email-dialog-sort-indicator" aria-hidden="true">
                                    {sort.direction === 'asc' ? '↑' : '↓'}
                                  </span>
                                )}
                                {isFilterableColumn(col) && (
                                  <FilterCell
                                    column={col}
                                    data={rows}
                                    value={filters[col.field] || {}}
                                    onChange={(type, value) => handleFilter(col.field, type, value)}
                                  />
                                )}
                              </div>
                            </div>
                          </th>
                        ))}
                        <th>הערה לשורה</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRowEntries.map(({ row, rowKey }) => {
                        const state = rowState[rowKey] || { selected: false, rowNote: '' };
                        return (
                          <tr key={rowKey} className={state.selected ? 'is-selected' : ''}>
                            <td className="col-check">
                              <input
                                type="checkbox"
                                checked={state.selected}
                                onChange={(e) => toggleRow(rowKey, e.target.checked)}
                                aria-label="בחר שורה"
                              />
                            </td>
                            {tableColumns.map((col) => (
                              <td key={col.columnId || col.field}>
                                {formatCellValue(row[col.field])}
                              </td>
                            ))}
                            <td>
                              <input
                                type="text"
                                className="boq-email-dialog-row-note"
                                value={state.rowNote}
                                disabled={!state.selected}
                                placeholder={state.selected ? 'הערה לשורה...' : ''}
                                onChange={(e) => setRowNote(rowKey, e.target.value)}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            <div className="boq-email-dialog-footer">
              <span className="boq-email-dialog-count">
                {canProceed
                  ? `${selectedCount} שורות יישלחו ל-${getReportRecipientLabel(recipientKey)}`
                  : 'יש לבחור לפחות שורה אחת'}
              </span>
              <div className="boq-email-dialog-footer-actions">
                <button type="button" className="boq-btn boq-btn-secondary" onClick={onClose}>
                  ביטול
                </button>
                <button
                  type="button"
                  className="boq-btn boq-btn-primary"
                  disabled={!canProceed}
                  onClick={() => setStep('confirm')}
                >
                  המשך לאישור
                </button>
              </div>
            </div>
          </>
        )}

        {step === 'confirm' && (
          <>
            <div className="boq-email-dialog-body boq-email-dialog-confirm">
              <div className="boq-email-dialog-confirm-box">
                <strong>סיכום שליחה (Mock)</strong>
                <ul>
                  <li>סוג בקשה: {getReportRequestTypeLabel(requestType)}</li>
                  <li>
                    נמען: {getReportRecipientLabel(recipientKey)} ({getReportRecipientEmail(recipientKey)})
                  </li>
                  <li>מספר שורות: {selectedCount}</li>
                  {generalNote.trim() && <li>הערה כללית: {generalNote.trim()}</li>}
                </ul>
              </div>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#666' }}>
                בגרסה הסופית הנתונים יישלחו ל-API: <code>{sendApiHint}</code>
              </p>
            </div>
            <div className="boq-email-dialog-footer">
              <span />
              <div className="boq-email-dialog-footer-actions">
                <button type="button" className="boq-btn boq-btn-secondary" onClick={() => setStep('form')}>
                  חזרה
                </button>
                <button type="button" className="boq-btn boq-btn-primary" onClick={handleConfirmSend}>
                  אשר ושלח (Mock)
                </button>
              </div>
            </div>
          </>
        )}

        {step === 'sending' && (
          <div className="boq-email-dialog-sending">
            {sending ? 'שולח (סימולציה)...' : 'הושלם'}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
